import { FC, useState } from 'react'
import { WorkflowRunGroup, RunStatus } from '@aero-platform/shared'
import { getCaseNumber } from '../../services/logDownloadService'
import { ReceiptIcon } from '../Icons'
import IconButton from '../buttons/IconButton'
import LogViewerModal from "./LogViewerModal"
import { Tooltip } from 'primereact/tooltip'

interface ViewLogsButtonProps {
  workflowRunGroup: WorkflowRunGroup
  raceTeamId: string
  onModalStateChange?: (isOpen: boolean) => void
}

/**
 * Button component to view live log stream for a case
 * Only shown when case status is Queued or Running
 * Opens a modal with streaming log viewer, auto-refresh, and download options
 */
const ViewLogsButton: FC<ViewLogsButtonProps> = ({
  workflowRunGroup,
  raceTeamId,
  onModalStateChange
}) => {
  const [showModal, setShowModal] = useState(false)

  const handleModalOpen = () => {
    setShowModal(true)
    onModalStateChange?.(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    onModalStateChange?.(false)
  }

  const caseNumber = getCaseNumber(workflowRunGroup)
  const status = workflowRunGroup.overallStatus

  // Show button for active and completed cases
  // Hide only for CREATED (not yet submitted) and UNSPECIFIED
  const shouldShow = status !== RunStatus.CREATED && 
                     status !== RunStatus.UNSPECIFIED &&
                     caseNumber !== null

  if (!shouldShow) {
    return null
  }

  // Determine if logs are likely available
  // Logs are typically available once the case is RUNNING or in a final state
  const logsAvailable = status === RunStatus.RUNNING ||
                        status === RunStatus.COMPLETED ||
                        status === RunStatus.ERROR ||
                        status === RunStatus.CANCELLED ||
                        status === RunStatus.WARN_ON_ERROR
  
  const tooltipId = `live-log-btn-${workflowRunGroup._id}`

  return (
    <>
      <div 
        data-pr-tooltip={!logsAvailable ? "Live log will appear shortly after the job starts." : "View Live Logs"}
        data-pr-position="top"
        className={tooltipId}
      >
        <IconButton
          title=""
          severity="info"
          icon={<ReceiptIcon className="fill-white h-3 w-3" />}
          onClick={handleModalOpen}
          disabled={!logsAvailable}
        />
      </div>
      <Tooltip target={`.${tooltipId}`} />
      
      {showModal && (
        <LogViewerModal
          visible={showModal}
          workflowRunGroup={workflowRunGroup}
          raceTeamId={raceTeamId}
          onHide={handleModalClose}
        />
      )}
    </>
  )
}

export default ViewLogsButton
